import React, { useState, useEffect } from "react";
import { compose } from "recompose";
import { Button, Table } from "reactstrap";

import {
  TableContainer,
  TableDataFetch,
  withTable,
  ViewingRecords,
  Pagination,
  BulkArchiveButton,
  BulkUndeleteButton,
} from "components/common/Tables/index";
import Page from "components/common/Page";
import { Trans, useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { ApiClient } from "services";
import { ColouredLink } from "components/HoverCard";
import LoadingMsg from "components/common/LoadingMsg";
import SuperUserStatsCard, {ExportButton, ModalTable, SearchBarWithDate, useSearchStates} from "../CoreComponents";
import BackButton from "components/refactoring/BackButton";
import { formatDate } from "services/util";

export const OrganisationList = ({
  selected,
  match,
  url = "core/organisation",
  setSelected,
  ordering,
  setOrdering,
  data,
  setData,
  limit,
  offset,
  search,
  filters,
  refreshToken,
  setOffset,
  setLimit,
  setSearch,
  setFilters,
  refreshData,
  readOnly,
}) => {
  const {t} = useTranslation()

  const [headers, setHeaders] = useState({})
  const [subsPlans, setSubsPlans] = useState()
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const history = useHistory()
  const api_client = ApiClient()

  const handleModalClose = () => {
    setShowModal(false);
  };
  
  function fetchHeader(){
    setLoading(true)
    api_client.get('subscription-plans').then(resp => {
      setSubsPlans(resp.data)
    })
    api_client.get('core/organisation-header-count/').then(resp => {
      setHeaders(resp.data)
      setLoading(false)
    }).catch(err =>{
      console.log(err)
      setLoading(false)
    })
  }

  function detailsLink(id) {
    history.push(`/core/organisation/${id}`);
  }

  const toggleArchived = () => {
    setOffset(0);
    setSelected({});
    const next = !showArchived;
    setShowArchived(next);
    setFilters({ ...filters, archived: next ? 'true' : undefined });
  };

  useEffect(() => {
    fetchHeader()
  }, [])

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const {
    searchFront,
    startDate,
    endDate,
    expiryStart,
    expiryEnd,
    selectedOption,
    hanStartDate,
    hanEndDate,
    hanExpiryStart,
    hanExpiryEnd,
    handInput,
    setSelectedOption,
    subscriptionStatus,
    handleSubscriptionStatus,
    resetSearchStates,
  } = useSearchStates();

  let errorNotificationShown = false;

  function incrementDay(date) {
    const nextDay = new Date(date);
    nextDay.setDate(nextDay.getDate() + 1);
    return nextDay;
  }

  const planOptions = subsPlans?.map(item => ({ label: t(item.name), value: item.id }));

  const buildDateAndStatusParams = () => {
    const params = {
      subscription_start_date_after: startDate ? formatDate(startDate) : undefined,
      subscription_start_date_before: endDate ? formatDate(endDate) : undefined,
      subscription_end_date_after: expiryStart ? formatDate(expiryStart) : undefined,
      subscription_end_date_before: expiryEnd ? formatDate(expiryEnd) : undefined,
      subscription_plan: selectedOption ? selectedOption.value : undefined,
      subscription_status: subscriptionStatus || undefined,
    };
    return params;
  };

  const applyFilters = () => {
    setSearch(searchFront);
    setFilters({
      ...filters,
      search: searchFront || undefined,
      card_filter: undefined,
      ...buildDateAndStatusParams(),
    });
  };

  const clearFilters = () => {
    resetSearchStates();
    setSearch(null);
    setFilters({
      ...filters,
      search: undefined,
      subscription_start_date_after: undefined,
      subscription_start_date_before: undefined,
      subscription_end_date_after: undefined,
      subscription_end_date_before: undefined,
      subscription_plan: undefined,
      subscription_status: undefined,
      card_filter: undefined,
    });
  };

  const handleFilterClick = (params = {}) => {
    handleSubscriptionStatus(params.subscription_status || null);
    setFilters({
      ...filters,
      search: undefined,
      subscription_start_date_after: undefined,
      subscription_start_date_before: undefined,
      subscription_end_date_after: undefined,
      subscription_end_date_before: undefined,
      subscription_plan: undefined,
      subscription_status: undefined,
      card_filter: undefined,
      ...params,
    });
  };

  const datesData = {
    searchFront,
    startDate,
    endDate,
    expiryStart,
    expiryEnd,
    selectedOption,
    subscriptionStatus,
  };

  const handleData = {
    handInput,
    hanStartDate,
    hanEndDate,
    hanExpiryEnd,
    hanExpiryStart,
    handleSubscriptionStatus,
  };

  return (
    <Page title="Organisations List" className="inv-table" style={{ maxWidth: '1800px', }}>
      <BackButton/>
      <div>
        {<>
          <SuperUserStatsCard headers={headers} onFilterClick={handleFilterClick} t={t}/>
          <div className="ml-1">
            <ColouredLink onClick={() => setShowModal(true)}><Trans>Invoice Details</Trans></ColouredLink>
            <ModalTable headers={headers} handleModalClose={handleModalClose} showModal={showModal}/>

            <ColouredLink onClick={toggleArchived} className="float-right">
              <Trans>{showArchived ? "Show Active" : "Show Archived"}</Trans>
            </ColouredLink>
          </div>

          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '5px', padding: '2.5px' }}>
            <SearchBarWithDate
              i={datesData}
              h={handleData}
              planOptions={planOptions}
              setSelectedOption={setSelectedOption}
              placeholder="Search by organisation, company or email"
              t={t}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: '5px', padding: '2.5px' }}>
            <ExportButton exportType="organisation" dropdownOpen={dropdownOpen} toggleDropdown={toggleDropdown} client={api_client} filters={filters} />

            {showArchived ?
              <BulkUndeleteButton
                selected={selected}
                url={url}
                onUndeleted={refreshData}
                client={api_client}
                readOnly={readOnly}
              />
              :
              <BulkArchiveButton
                selected={selected}
                url={url}
                onArchived={refreshData}
                client={api_client}
                readOnly={readOnly}
              />
            }
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '5px' }}>
              <Button color="primary" size="md" onClick={applyFilters}>
                <Trans>Apply Filters</Trans>
              </Button>
              <Button color="secondary" size="md" onClick={clearFilters}>
                <Trans>Clear</Trans>
              </Button>
            </div>
          </div>
        </>}

        {!data.loading &&
        <TableContainer
          items={data?.results || []}
          ordering={ordering}
          onOrderingChanged={setOrdering}
          selected={selected}
          onSelectedChanged={setSelected}
        >
            <Table responsive>
            <thead>
              <tr>
                <th select />
                <th className="text-left">
                  <Trans>Organisation</Trans>
                </th>
                <th>
                  <Trans>Company</Trans>
                </th>
                <th>
                  <Trans>Primary Email</Trans>
                </th>
                <th>
                  <Trans>Xero Organisation Name</Trans>
                </th>
                <th>
                  <Trans>Plan</Trans>
                </th>
                <th>
                  <Trans>Number of Organisations</Trans>
                </th>
                <th>
                  <Trans>Start Date</Trans>
                </th>
                <th>
                  <Trans>Expiration</Trans>
                </th>
                <th>
                  <Trans>Last Invoice Number</Trans>
                </th>
                <th>
                  <Trans>Next Invoice Date</Trans>
                </th>
                <th>
                  <Trans>Users</Trans>
                </th>
                <th>
                  <Trans>Subscription</Trans>
                </th>
                <th>
                  <Trans>Status</Trans>
                </th>
                <th>
                  <Trans>Xero Connection</Trans>
                </th>
              </tr>
            </thead>
            <tbody>
                {data?.results?.map((i) => (
                <tr key={i.id}>
                  <td select /> 
                  <td className="text-left"><ColouredLink onClick={() => detailsLink(i.id)}>{i.name}</ColouredLink></td>
                  <td>{i.company?.company_name}</td>
                  <td>{i.subscriber_user?.email}</td>
                  <td>{i.xero_organisation_name}</td>
                  <td>{i.subscription_detail?.current_subscription?.subscription_plan_name || (i.subscriber_user?.is_trial_active && 'Trial') || '-'}</td>
                  <td>{i.number_of_organisations}</td>
                  <td>{i.subscription_detail?.current_subscription?.start_date || (i.subscriber_user?.is_trial_active && i.subscriber_user?.date_joined) || '-' }</td>
                  <td>{i.subscription_detail?.current_subscription?.end_date || (i.subscriber_user?.is_trial_active && i.subscriber_user?.trial_ends_date) || '-' }</td>
                  <td>{i.last_invoice_number || '-'}</td>
                  <td>{i.subscription_detail?.current_subscription?.next_invoice_date || '-'}</td>
                  <td>{i.total_user || '-'}</td>
                  <td>{i.subscription_detail?.current_subscription?.status || '-'} { i.subscription_detail?.subscription_type && `(${i.subscription_detail?.subscription_type})` || ''}</td>
                  <td>{i.deleted ? 'Archived' : 'Active'}</td>

                  <td style={{ color: 
                      i.status === 'LIVE' ? 'green' :
                      i.status === 'NOT_CONNECTED' ? 'red' :
                      'orange',
                  }}>
                    <strong>
                      {
                        i.status === 'LIVE' ? 'LIVE' : i.status === 'NOT_CONNECTED' ? 'DISCONNECTED' : 'EXPIRED'
                      }
                    </strong>
                  </td>
                </tr>
              ))}
            </tbody>
          
          </Table>
        </TableContainer>}
        <TableDataFetch
          ordering={ordering}
          refreshToken={refreshToken}
          search={search}
          limit={limit}
          url={url}
          offset={offset}
          onDataFetched={setData}
          filters={filters}
        />
        {!data.loading && <ViewingRecords
          offset={offset}
          limit={limit}
          count={data.count}
          className="float-left"
        />}
        <div className="float-right">
          <Pagination
            size="sm"
            count={data.count}
            limit={limit}
            offset={offset}
            onOffsetChanged={setOffset}
            onLimitChanged={setLimit}
          />
        </div>
      </div>
    </Page>
  )};

const enhance = compose(withTable({ limit: 25 }));

export default enhance(OrganisationList);
